/* Size the complete Teeinblue preview; preserve native artwork coordinates. */
(() => {
  'use strict';
  if (window.FizzyDesktopGallery) return;

  const instances = new Map();
  const desktop = window.matchMedia('(min-width: 769px)');
  const lifecycle = new AbortController();
  const properties = ['--fizzy-gallery-top', '--fizzy-gallery-width', '--fizzy-gallery-height', '--fizzy-gallery-scale'];
  let frame = 0;

  function schedule() {
    if (!frame) frame = requestAnimationFrame(() => {
      frame = 0;
      instances.forEach(instance => instance.update());
    });
  }

  class ProductGallery {
    constructor(root) {
      this.root = root;
      this.resize = new ResizeObserver(schedule);
      this.resize.observe(root);
      // Teeinblue replaces its gallery. Observe this product only, not the page.
      const Observer = window.FizzyProductObserver?.Observer || MutationObserver;
      this.structure = new Observer(records => {
        if (records.some(record => (record.type === 'attributes' &&
          (record.target === root || record.target.matches('.tee-slide, .tee-mockup, .tee-zoom-normal'))) || [...record.addedNodes, ...record.removedNodes].some(node =>
          node.nodeType === 1 && (node.matches('#tee-gallery, .tee-gallery, .tee-slide, .tee-mockup') || node.querySelector('#tee-gallery, .tee-gallery, .tee-slide, .tee-mockup'))))) schedule();
      });
      this.structure.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
    }

    clear() {
      if (!this.column) return;
      this.column.removeAttribute('data-fizzy-desktop-gallery');
      properties.forEach(property => this.column.style.removeProperty(property));
    }

    clearMobile() {
      if (!this.mobileSlider) return;
      this.mobileSlider.removeAttribute('data-fizzy-mobile-fill');
      this.mobileSlider.style.removeProperty('--clean-mobile-preview-scale');
      this.mobileSlider.style.removeProperty('--clean-mobile-preview-height');
      this.mobileSlider = null;
    }

    updateMobile() {
      if (desktop.matches || !this.root.isConnected ||
          !this.root.hasAttribute('data-fizzy-clean-pdp') ||
          this.root.closest('[role="dialog"], .modal--quick-shop')) {
        this.clearMobile(); return;
      }
      // Keep the fill while sticky. CSS ignores it there, and clearing it would shrink the
      // gallery for one frame on exit, which scroll anchoring turns into a visible jump.
      if (this.root.classList.contains('tee-gallery-sticky')) return;
      const slider = this.root.querySelector('#tee-gallery .tee-slider');
      const mockup = slider?.querySelector('.tee-slide--active .tee-zoom-normal .tee-mockup');
      if (!mockup || !(mockup.offsetWidth > 0 && mockup.offsetHeight > 0 && slider.clientWidth > 0)) {
        this.clearMobile(); return;
      }
      if (this.mobileSlider !== slider) { this.clearMobile(); this.mobileSlider = slider; }
      const requested = Number.parseFloat(getComputedStyle(this.root).getPropertyValue('--clean-mobile-gallery-scale')) || 1;
      // Never let the complete mockup extend beyond the visible width. Wide
      // photos stay at native scale; tall mockups gain height instead of cropping.
      const scale = Math.max(1, Math.min(1.15, requested, slider.clientWidth / mockup.offsetWidth));
      const values = { '--clean-mobile-preview-scale': String(scale),
        '--clean-mobile-preview-height': `${Math.max(slider.clientWidth, mockup.offsetHeight * scale)}px` };
      Object.entries(values).forEach(([key, value]) => {
        if (slider.style.getPropertyValue(key) !== value) slider.style.setProperty(key, value);
      });
      slider.setAttribute('data-fizzy-mobile-fill', '');
    }

    update() {
      this.updateMobile();
      if (!desktop.matches || !this.root.isConnected || this.root.closest('[role="dialog"], .modal--quick-shop')) {
        this.clear();
        return;
      }
      const galleries = this.root.querySelectorAll('#tee-gallery');
      const gallery = galleries.length === 1 ? galleries[0] : null;
      const preview = gallery?.querySelector(':scope > .tee-gallery');
      const column = gallery?.closest('.product-single__sticky');
      // Unknown app layouts retain their native behavior.
      if (!preview || !column || !this.root.contains(column)) {
        this.clear();
        return;
      }
      if (this.preview !== preview || this.column !== column) {
        this.clear();
        if (this.preview) this.resize.unobserve(this.preview);
        this.preview = preview;
        this.column = column;
        this.resize.observe(preview);
      }

      const width = gallery.getBoundingClientRect().width;
      // Layout height stays unscaled; dividing visual bounds by rounded CSS zoom drifts.
      const naturalHeight = preview.offsetHeight;
      if (!(width > 0 && naturalHeight > 0)) { this.clear(); return; }

      const header = document.querySelector('#SiteHeader[data-sticky="true"]');
      let top = 20;
      if (header) {
        const bounds = header.getBoundingClientRect();
        const style = getComputedStyle(header);
        const headerTop = Number.parseFloat(style.top) || 0;
        const announcement = Number.parseFloat(style.getPropertyValue('--sticky-announcement-bar-height')) || 0;
        // The header animates in from above. Reserve its final height even mid-animation.
        const bottom = Math.max(headerTop + bounds.height + announcement,
          header.classList.contains('site-header--stuck') ? bounds.bottom : 0);
        top = Math.max(top, bottom + 12);
      }
      const available = window.innerHeight - top - 20;
      // Do not shrink an unknown or unusable layout to an invalid size.
      if (available <= 0) { this.clear(); return; }
      // The opt-in PDP may use the full column. Scale the complete app preview,
      // never individual artwork layers; tall previews then scroll in normal flow.
      const fullSize = this.root.hasAttribute('data-fizzy-clean-pdp') &&
        getComputedStyle(this.root).getPropertyValue('--clean-gallery-fit').trim() === '0';
      const scale = fullSize ? 1 : Math.min(1, available / naturalHeight);
      const values = [`${top}px`, `${width}px`, `${naturalHeight * scale}px`, String(scale)];
      properties.forEach((property, index) => {
        if (column.style.getPropertyValue(property) !== values[index]) column.style.setProperty(property, values[index]);
      });
      column.setAttribute('data-fizzy-desktop-gallery', '');
    }

    destroy() {
      this.clearMobile();
      this.clear();
      this.resize.disconnect();
      this.structure.disconnect();
    }
  }

  function mount() {
    bindHeader();
    instances.forEach((instance, root) => {
      if (!root.isConnected) { instance.destroy(); instances.delete(root); }
    });
    document.querySelectorAll('[data-teeinblue-pdp]').forEach(root => {
      if (!instances.has(root)) instances.set(root, new ProductGallery(root));
    });
    schedule();
  }

  let header;
  const headerResize = new ResizeObserver(schedule);
  const headerState = new MutationObserver(schedule);
  function bindHeader() {
    const next = document.querySelector('#SiteHeader');
    if (header === next) return;
    headerResize.disconnect(); headerState.disconnect();
    header = next;
    if (header) {
      headerResize.observe(header);
      headerState.observe(header, { attributes: true, attributeFilter: ['class', 'style'] });
    }
  }
  const options = { passive: true, signal: lifecycle.signal };
  window.addEventListener('scroll', () => { if (desktop.matches) schedule(); }, options);
  window.addEventListener('resize', schedule, options);
  document.addEventListener('teeinblue-event-component-injected', mount, options);
  document.addEventListener('shopify:section:load', mount, options);
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, root) => {
      if (event.target === root || event.target.contains(root)) { instance.destroy(); instances.delete(root); }
    });
  }, options);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, options);
  else mount();
  window.FizzyDesktopGallery = { destroy() {
    lifecycle.abort(); cancelAnimationFrame(frame);
    headerResize.disconnect(); headerState.disconnect();
    instances.forEach(instance => instance.destroy()); instances.clear();
    delete window.FizzyDesktopGallery;
  } };
})();
